import {
  StakingContract,
  useUser,
  useWithdrawAction,
} from '@trustpad/launchpad';
import { StakingContractV2 } from '@trustpad/launchpad/dist/staking/hooks/useStakingContract';

import BN from 'bn.js';
import dayjs from 'dayjs';
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import { FaLock } from 'react-icons/fa';

import { CustomInput } from '~common/CustomInput';
import { useModalProvider } from '~common/hooks/useModalProvider';
import { MaxButton } from '~common/UI';
import { DateTimeFormat, ZERO_BN } from '~config/constants';
import { TPAD } from '~config/tokens';
import { ActionButton, Dialog } from '~features/ui';
import { fNum, isValidValue, toEth, toWeiBN } from '~utils';

const isLocked = (time) => time > Date.now() / 1000;

export function UnstakeBlock({
  userStats,
  refreshAll,
  stakingContract,
  fee,
  allowEarlyWithdrawal,
  halted,
}: {
  userStats: { unlocksAt: number; amount: BN };
  refreshAll: VoidFunction;
  stakingContract: StakingContract | StakingContractV2;
  fee?: BN;
  allowEarlyWithdrawal?: boolean;
  halted?: boolean;
}) {
  const { account } = useUser();
  const { setMessage } = useModalProvider();
  const { withdraw, isLoading: isWithdrawing } = useWithdrawAction({
    contract: stakingContract,
    decimals: TPAD.decimals,
    refresh: () => {},
  });

  const [value, setValue] = useState('');
  const [locked, setLocked] = useState(() => isLocked(userStats.unlocksAt));
  const cancelButtonRef = useRef(null);

  const canWithdraw = !halted && (!locked || allowEarlyWithdrawal);
  const feeN =
    fee?.gtn(0) && locked && allowEarlyWithdrawal ? fee.divn(10).toNumber() : 0;
  const unlockDate = dayjs.unix(userStats.unlocksAt).utc();
  const totalAmount = value && isValidValue(value) ? toWeiBN(value) : ZERO_BN;
  const feeAmount = totalAmount.mul(fee).divn(1000);
  const amounts = {
    feePercent: feeN,
    totalAmount,
    feeAmount,
    endAmount: totalAmount.sub(feeAmount),
  };

  const onUnstake = () => withdraw(value).then(setMessage).then(refreshAll);

  useEffect(() => {
    setLocked(isLocked(userStats.unlocksAt));

    if (!locked) return;
    const id = setTimeout(
      () => setLocked(isLocked(userStats.unlocksAt)),
      Math.abs(userStats.unlocksAt - Date.now() / 1000),
    );
    return () => clearInterval(id);
  }, [locked, userStats.unlocksAt]);

  return (
    <div className="gap-6 flex items-center flex-wrap">
      <div className="flex-shrink-0 flex-grow">
        <CustomInput
          name="unstakeValue"
          value={value}
          onChange={setValue}
          topLabel={`Staked: ${fNum(userStats.amount, 8)} TPAD`}
          button={
            <MaxButton
              label="Max"
              onClick={() => setValue(toEth(userStats.amount))}
            />
          }
          icon={<span className="text-gray-400">TPAD</span>}
        />
      </div>

      <Dialog
        openButton={(open) => (
          <ActionButton
            isLoading={isWithdrawing}
            isDisabled={
              !canWithdraw ||
              isWithdrawing ||
              !value ||
              !account ||
              toWeiBN(value).gt(userStats.amount) ||
              halted
            }
            onClick={open}
            className="w-28">
            Unstake {amounts.feePercent > 0 && `(fee ${amounts.feePercent}%)`}{' '}
            {!canWithdraw && (
              <FaLock className="inline text-xs align-sub ml-1" />
            )}
          </ActionButton>
        )}
        title="Unstake tokens?"
        initialFocus={cancelButtonRef}
        buttons={({ close }) => (
          <>
            <Button
              variant="outline-secondary"
              className="w-full"
              ref={cancelButtonRef}
              onClick={close}>
              Cancel
            </Button>
            <Button onClick={() => onUnstake().then(close)} className="w-full">
              Unstake
            </Button>
          </>
        )}>
        <Dialog.Sections>
          <Dialog.SectionItem iconStatus="warning">
            <p>
              Important: If you unstake and do not re-stake at least the same
              amount of $TPAD within 72 hours, all your vested tokens (incl.
              future distributions) in projects you have participated in, gets
              forfeited and re-distributed to loyal stakers. Read{' '}
              <a
                href="https://trustpad.medium.com/faq-frequently-asked-questions-for-diamond-hands-rules-90309648cade"
                className="text-white font-normal">
                FAQ here
              </a>
              .
            </p>
          </Dialog.SectionItem>
          {amounts.feePercent > 0 && (
            <Dialog.SectionItem iconStatus="warning">
              <p>
                Your tokens are locked until{' '}
                <b>{unlockDate.format(DateTimeFormat.noYear)}</b>. If you
                unstake <b>{fNum(amounts.totalAmount)} TPAD</b> now:
              </p>
              <ul className="list-disc list-inside">
                <li>
                  you will receive <b>{fNum(amounts.endAmount)} TPAD</b>
                </li>
                <li>
                  you will{' '}
                  <span className="text-red-500">
                    lose {amounts.feePercent}%
                  </span>{' '}
                  ={' '}
                  <b className="text-red-500">{fNum(amounts.feeAmount)} TPAD</b>
                </li>
              </ul>
              <p className="mt-4">
                Are you sure you want to unstake? The fee cannot be refunded
                once the transaction is done.
              </p>
            </Dialog.SectionItem>
          )}
        </Dialog.Sections>
      </Dialog>
    </div>
  );
}
